import React, { useState } from "react";
import moment from "moment";
import { PiSquaresFour } from "react-icons/pi";
import {
  IoAlertCircleOutline,
  IoTimeOutline,
  IoCheckmarkCircleOutline,
  IoCheckmarkDoneOutline,
} from "react-icons/io5";
import {
  getStatusStyles,
  getPriorityStyles,
  getSourceTypeLabel,
  formatEnumForDisplay,
  getMarkupFromMetadata,
  DEFAULT_NO_IMAGE,
} from "./constants";
import ImageLightbox from "./ImageLightbox";

const IssueStatusIcon = ({ status }) => {
  const s = (status || "").toUpperCase().replace(/\s+/g, "_");
  if (s === "OPEN")
    return <IoAlertCircleOutline color="#dc2626" size={14} aria-hidden />;
  if (s === "IN_PROGRESS")
    return <IoTimeOutline color="#ea580c" size={14} aria-hidden />;
  if (s === "RESOLVED")
    return <IoCheckmarkCircleOutline color="#16a34a" size={14} aria-hidden />;
  if (s === "DONE")
    return <IoCheckmarkDoneOutline color="#fff" size={14} aria-hidden />;
  return null;
};

const GRID_COLUMNS_WITH_SOURCE = "80px 2fr 1fr 1fr 1fr 1fr 1fr 1fr";
const GRID_COLUMNS_WITHOUT_SOURCE = "80px 2fr 1fr 1fr 1fr 1fr 1fr";

const cellText = { fontSize: 14, color: "#4b5563", minWidth: 0 };
const pillBase = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "4px 10px",
  borderRadius: 9999,
  fontSize: 12,
  fontWeight: 500,
  textTransform: "capitalize",
  width: "fit-content",
};

export default function ProjectListIssues({ list, onIssueClick, hideSourceColumn = false }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const gridCols = hideSourceColumn
    ? GRID_COLUMNS_WITHOUT_SOURCE
    : GRID_COLUMNS_WITH_SOURCE;

  return (
    <div style={{ overflow: "hidden", marginTop: 16 }} className="issues-list-root">
      <style>{`
        @media (max-width: 899px) {
          .issues-list-root .issues-table { display: none; }
        }
        @media (min-width: 900px) {
          .issues-list-root .issues-cards { display: none; }
        }
      `}</style>
      <div className="issues-table">
      <div
        style={{
          display: "grid",
          gridTemplateColumns: gridCols,
          gap: 8,
          padding: "12px 16px",
          fontSize: 13,
          color: "#949daa",
        }}
      >
        <div />
        <div>Issue Description</div>
        {!hideSourceColumn && <div>Source</div>}
        <div>Activity</div>
        <div>Activity Type</div>
        <div>Status</div>
        <div>Severity</div>
        <div>Date Logged</div>
      </div>

      <div style={{ display: "flex", flexDirection: "column" }}>
        {list.map((issue) => (
          <div
            key={issue.id}
            role="button"
            tabIndex={0}
            onClick={() => onIssueClick?.(issue.id)}
            onKeyDown={(e) => e.key === "Enter" && onIssueClick?.(issue.id)}
            style={{
              display: "grid",
              gridTemplateColumns: gridCols,
              gap: 8,
              alignItems: "center",
              padding: "14px 16px",
              borderBottom: "1px solid #f3f4f6",
              cursor: "pointer",
              borderRadius: 6,
              transition: "background-color 120ms ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f9fafb")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          >
            <div
              style={{
                position: "relative",
                width: 64,
                height: 64,
                borderRadius: 10,
                overflow: "hidden",
                backgroundColor: "#e5e7eb",
                flexShrink: 0,
                cursor: issue.image_url ? "zoom-in" : "default",
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (issue.image_url) {
                  setLightboxImage(issue.image_url);
                  setLightboxOpen(true);
                }
              }}
            >
              <img
                src={issue.image_url || DEFAULT_NO_IMAGE}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
              {(() => {
                const markup = getMarkupFromMetadata(issue.metadata);
                return markup != null ? (
                  <span
                    style={{
                      position: "absolute",
                      bottom: 2,
                      right: 2,
                      minWidth: 18,
                      height: 18,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "0 4px",
                      borderRadius: 9999,
                      backgroundColor: "#2563eb",
                      color: "#fff",
                      fontSize: 10,
                      fontWeight: 500,
                    }}
                  >
                    {markup}
                  </span>
                ) : null;
              })()}
            </div>

            <div style={{ minWidth: 0 }}>
              <p
                style={{
                  fontWeight: 500,
                  color: "#0d0d0e",
                  fontSize: 14,
                  margin: 0,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {issue.description || "-"}
              </p>
              <p
                style={{
                  fontSize: 12,
                  color: "#6b7280",
                  margin: 0,
                  marginTop: 2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {issue.issue_category
                  ? `${issue.issue_category.activityType || ""} • ${issue.issue_category.category || ""}`
                  : "-"}
              </p>
            </div>

            {!hideSourceColumn && (
              <div style={{ minWidth: 0 }}>
                <span
                  style={{
                    ...pillBase,
                    backgroundColor: "#f3f4f6",
                    color: "#374151",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  {getSourceTypeLabel(issue.source_type)}
                </span>
              </div>
            )}

            <div style={cellText}>
              {issue?.activity?.milestoneName || "-"}
            </div>

            <div style={{ ...cellText, display: "flex", alignItems: "center", gap: 4 }}>
              <PiSquaresFour size={18} /> {issue?.issue_category?.activityType || "-"}
            </div>

            <div style={{ minWidth: 0 }}>
              <span style={{ ...pillBase, ...getStatusStyles(issue.status) }}>
                <IssueStatusIcon status={issue.status} />
                {formatEnumForDisplay(issue.status) || "-"}
              </span>
            </div>

            <div style={{ minWidth: 0 }}>
              <span
                style={{
                  ...pillBase,
                  ...getPriorityStyles(issue.issue_category?.severity),
                }}
              >
                {formatEnumForDisplay(issue.issue_category?.severity) || "-"}
              </span>
            </div>

            <div style={cellText}>
              {issue.created_at ? moment(issue.created_at).format("MMM DD, YYYY") : "-"}
            </div>
          </div>
        ))}
      </div>
      </div>

      {/* Mobile cards — same data, stacked vertically. */}
      <div className="issues-cards" style={{ display: "flex", flexDirection: "column" }}>
        {list.map((issue) => (
          <div
            key={`m-${issue.id}`}
            role="button"
            tabIndex={0}
            onClick={() => onIssueClick?.(issue.id)}
            onKeyDown={(e) => e.key === "Enter" && onIssueClick?.(issue.id)}
            style={{
              background: "#fff",
              borderBottom: "1px solid #e5e7eb",
              padding: "14px 0",
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {/* Row 1 — image · issue name (centered) · status pill. */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div
                style={{
                  position: "relative",
                  width: 56,
                  height: 56,
                  borderRadius: 10,
                  overflow: "hidden",
                  backgroundColor: "#e5e7eb",
                  flexShrink: 0,
                  cursor: issue.image_url ? "zoom-in" : "default",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (issue.image_url) {
                    setLightboxImage(issue.image_url);
                    setLightboxOpen(true);
                  }
                }}
              >
                <img
                  src={issue.image_url || DEFAULT_NO_IMAGE}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  textAlign: "left",
                  paddingLeft: 4,
                }}
              >
                <p
                  style={{
                    fontWeight: 600,
                    color: "#0d0d0e",
                    fontSize: 14,
                    margin: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {issue.description || "-"}
                </p>
                <p
                  style={{
                    fontSize: 12,
                    color: "#6b7280",
                    margin: 0,
                    marginTop: 2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {issue?.activity?.milestoneName || "-"}
                </p>
              </div>
              <span
                style={{
                  ...pillBase,
                  ...getStatusStyles(issue.status),
                  flexShrink: 0,
                }}
              >
                <IssueStatusIcon status={issue.status} />
                {formatEnumForDisplay(issue.status) || "-"}
              </span>
            </div>

            {/* Row 2 — severity + source pills sized to content, equally spaced. */}
            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "space-between",
              }}
            >
              <span
                style={{
                  ...pillBase,
                  ...getPriorityStyles(issue.issue_category?.severity),
                }}
              >
                {formatEnumForDisplay(issue.issue_category?.severity) || "-"}
              </span>
              {!hideSourceColumn && (
                <span
                  style={{
                    ...pillBase,
                    backgroundColor: "#f3f4f6",
                    color: "#374151",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  {getSourceTypeLabel(issue.source_type)}
                </span>
              )}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
                fontSize: 12,
                color: "#6b7280",
              }}
            >
              <div>
                <div style={{ color: "#9ca3af", marginBottom: 2 }}>Activity Type</div>
                <div style={{ color: "#0d0d0e", display: "flex", alignItems: "center", gap: 4 }}>
                  <PiSquaresFour size={16} />
                  {issue?.issue_category?.activityType || "-"}
                </div>
              </div>
              <div>
                <div style={{ color: "#9ca3af", marginBottom: 2 }}>Date Logged</div>
                <div style={{ color: "#0d0d0e" }}>
                  {issue.created_at ? moment(issue.created_at).format("MMM DD, YYYY") : "-"}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <ImageLightbox
        isOpen={lightboxOpen}
        onClose={() => {
          setLightboxOpen(false);
          setLightboxImage(null);
        }}
        images={lightboxImage}
      />
    </div>
  );
}
